import type { AudioManagerPort } from '../services/audio';
import type { LeaderboardSeasonId } from '../leaderboard/types';
import { EXTREME_PAID_SPINS } from '../extreme/types';
import { AudioSettingsController } from './audio-settings';
import type { ChatUiPort } from './chat-dialog';
import { ChatDialogController } from './chat-dialog';
import { formatCents, formatEuro, sanitizePlayerName } from './format';
import { DEFAULT_HOTKEYS, HotkeyController } from './hotkeys';
import type { AppShellCallbacks, AppShellPort, AppViewModel, DialogKind, GameMode, StatusTone } from './types';

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`UI-Element fehlt: ${selector}`);
  return element;
}

function invoke(operation: () => void | Promise<void>): void {
  try {
    const result = operation();
    if (result instanceof Promise) void result.catch(() => undefined);
  } catch {
    // The game controller owns operational error reporting.
  }
}

const DIALOG_SELECTORS: Readonly<Record<DialogKind, string>> = {
  audio: '[data-audio-dialog]',
  info: '[data-info-dialog]',
  leaderboard: '[data-leaderboard-dialog]',
  chat: '[data-chat-dialog]',
  ranked: '[data-ranked-dialog]',
  replay: '[data-replay-dialog]',
  'feature-intro': '[data-feature-intro-dialog]',
  'feature-summary': '[data-feature-summary-dialog]',
  'extreme-summary': '[data-extreme-summary-dialog]',
  'extreme-abandon': '[data-extreme-abandon-dialog]',
};

export interface CreateAppShellOptions {
  readonly audio?: AudioManagerPort;
  readonly chat?: ChatUiPort;
  readonly storage?: Storage;
}

export class AppShell implements AppShellPort {
  private readonly root: HTMLElement;
  private readonly balance: HTMLOutputElement;
  private readonly lastWin: HTMLOutputElement;
  private readonly betSelect: HTMLSelectElement;
  private readonly spin: HTMLButtonElement;
  private readonly auto: HTMLButtonElement;
  private readonly turbo: HTMLButtonElement;
  private readonly modeMenu: HTMLButtonElement;
  private readonly normalMode: HTMLButtonElement;
  private readonly rankedMode: HTMLButtonElement;
  private readonly extremeMode: HTMLButtonElement;
  private readonly modeSummary: HTMLElement;
  private readonly modeNote: HTMLElement;
  private readonly rankedBetSelect: HTMLSelectElement;
  private readonly rankedRunStatus: HTMLElement;
  private readonly rankedEndDialog: HTMLButtonElement;
  private readonly rankedConfigElements: readonly HTMLElement[];
  private readonly extremeRunStatus: HTMLElement;
  private readonly extremeConfigElements: readonly HTMLElement[];
  private readonly status: HTMLElement;
  private readonly statusText: HTMLElement;
  private readonly boardDescription: HTMLElement;
  private readonly featureMeter: HTMLElement;
  private readonly freeSpins: HTMLElement;
  private readonly stickyCount: HTMLElement;
  private readonly featureWin: HTMLOutputElement;
  private readonly dialogs = new Map<DialogKind, HTMLDialogElement>();
  private readonly cleanup: Array<() => void> = [];
  private readonly hotkeys: HotkeyController;
  private view: AppViewModel;
  private displayedRankedX: number | null = null;
  private displayedExtremeX: number | null = null;
  private season: LeaderboardSeasonId = 'season3';
  private leaderboardMode: 'ranked' | 'extreme' = 'ranked';
  private board: 'runs' | 'freeSpins' = 'runs';

  constructor(
    private readonly documentRoot: Document,
    private readonly callbacks: AppShellCallbacks,
    options: CreateAppShellOptions = {},
  ) {
    this.root = requireElement(documentRoot, '[data-app-shell]');
    this.balance = requireElement(documentRoot, '[data-balance]');
    this.lastWin = requireElement(documentRoot, '[data-last-win]');
    this.betSelect = requireElement(documentRoot, '[data-bet-select]');
    this.spin = requireElement(documentRoot, '[data-spin-button]');
    this.auto = requireElement(documentRoot, '[data-auto-button]');
    this.turbo = requireElement(documentRoot, '[data-turbo-button]');
    this.modeMenu = requireElement(documentRoot, '[data-mode-menu-button]');
    this.normalMode = requireElement(documentRoot, '[data-mode-normal]');
    this.rankedMode = requireElement(documentRoot, '[data-mode-ranked]');
    this.extremeMode = requireElement(documentRoot, '[data-mode-extreme]');
    this.modeSummary = requireElement(documentRoot, '[data-mode-summary]');
    this.modeNote = requireElement(documentRoot, '[data-mode-note]');
    this.rankedBetSelect = requireElement(documentRoot, '[data-ranked-bet-select]');
    this.rankedRunStatus = requireElement(documentRoot, '[data-ranked-run-status]');
    this.rankedEndDialog = requireElement(documentRoot, '[data-ranked-end-dialog]');
    this.rankedConfigElements = [
      requireElement<HTMLElement>(documentRoot, '[data-ranked-intro]'),
      requireElement<HTMLInputElement>(documentRoot, '[data-ranked-name]').parentElement!,
      requireElement<HTMLSelectElement>(documentRoot, '[data-ranked-bet-select]').parentElement!,
      requireElement<HTMLElement>(documentRoot, '.ranked-summary'),
      requireElement<HTMLButtonElement>(documentRoot, '[data-ranked-start]'),
    ];
    this.extremeRunStatus = requireElement(documentRoot, '[data-extreme-run-status]');
    this.extremeConfigElements = [
      requireElement<HTMLElement>(documentRoot, '[data-extreme-intro]'),
      requireElement<HTMLInputElement>(documentRoot, '[data-extreme-name]').parentElement!,
      requireElement<HTMLElement>(documentRoot, '.extreme-config-summary'),
      requireElement<HTMLButtonElement>(documentRoot, '[data-extreme-start]'),
    ];
    this.status = requireElement(documentRoot, '[data-status]');
    this.statusText = requireElement(documentRoot, '[data-status-text]');
    this.boardDescription = requireElement(documentRoot, '[data-board-description]');
    this.featureMeter = requireElement(documentRoot, '[data-feature-meter]');
    this.freeSpins = requireElement(documentRoot, '[data-free-spins]');
    this.stickyCount = requireElement(documentRoot, '[data-sticky-count]');
    this.featureWin = requireElement(documentRoot, '[data-feature-win]');
    Object.entries(DIALOG_SELECTORS).forEach(([kind, selector]) => {
      this.dialogs.set(kind as DialogKind, requireElement(documentRoot, selector));
    });
    this.view = {
      ready: false, balanceCents: 100_000, betCents: 200, lastWinCents: 0,
      gameMode: 'normal', presentationMode: 'cards', auto: false, turbo: false,
      spinning: false, interactionLocked: false, canSpin: false, canChangeBet: false,
      status: 'Der Tempel wird vorbereitet …', statusTone: 'neutral', feature: null, rankedRun: null, extremeRun: null,
    };
    this.hotkeys = new HotkeyController(DEFAULT_HOTKEYS, () => ({
      dialogOpen: [...this.dialogs.values()].some((dialog) => dialog.open),
      spinning: this.view.spinning,
      autoActive: this.view.auto,
      canSpin: this.view.canSpin,
      canToggleAuto: this.view.ready && (!this.view.spinning || this.view.auto),
      canToggleTurbo: this.view.ready && !this.view.spinning && !this.view.interactionLocked,
    }), {
      spin: () => invoke(callbacks.onSpin),
      auto: () => callbacks.onAutoChange(!this.view.auto),
      turbo: () => callbacks.onTurboChange(!this.view.turbo),
    });
    this.bind(options);
    this.render(this.view);
  }

  render(viewModel: AppViewModel): void {
    this.view = viewModel;
    this.balance.value = formatCents(viewModel.balanceCents);
    this.lastWin.value = formatCents(viewModel.lastWinCents);
    this.betSelect.value = String(viewModel.betCents);
    this.rankedBetSelect.value = String(viewModel.betCents);
    if (viewModel.rankedRun === null) this.displayedRankedX = null;
    else if (!viewModel.spinning) this.displayedRankedX = viewModel.rankedRun.currentX;
    if (viewModel.extremeRun === null) this.displayedExtremeX = null;
    else if (!viewModel.spinning) this.displayedExtremeX = viewModel.extremeRun.currentX;
    const modeLabel = viewModel.gameMode === 'extreme' ? 'Extreme' : viewModel.gameMode === 'ranked' ? 'Ranked' : 'Normal';
    this.modeSummary.textContent = `${modeLabel} · ${formatEuro(viewModel.betCents)}`;
    this.modeNote.textContent = viewModel.extremeRun !== null
      ? `${viewModel.extremeRun.username} · ${viewModel.extremeRun.paidSpinsRemaining} Spins · ${(this.displayedExtremeX ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`
      : viewModel.rankedRun === null
        ? 'Freies Spiel'
        : `${viewModel.rankedRun.username} · ${(this.displayedRankedX ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
    this.auto.setAttribute('aria-pressed', String(viewModel.auto));
    this.turbo.setAttribute('aria-pressed', String(viewModel.turbo));
    requireElement(this.auto, '[data-auto-state]').textContent = viewModel.auto ? 'Aktiv' : 'Aus';
    requireElement(this.turbo, '[data-turbo-state]').textContent = viewModel.turbo ? 'Aktiv' : 'Aus';
    this.spin.disabled = !viewModel.ready || !viewModel.canSpin || viewModel.auto;
    const autoStopRemainsAvailable = viewModel.spinning && viewModel.auto;
    this.auto.disabled = !viewModel.ready
      || (viewModel.interactionLocked && !autoStopRemainsAvailable)
      || (viewModel.spinning && !viewModel.auto);
    this.turbo.disabled = !viewModel.ready || viewModel.interactionLocked || viewModel.spinning;
    this.betSelect.disabled = !viewModel.ready || !viewModel.canChangeBet || viewModel.auto;
    this.rankedBetSelect.disabled = !viewModel.ready || !viewModel.canChangeBet || viewModel.auto;
    this.modeMenu.disabled = !viewModel.ready || viewModel.interactionLocked || viewModel.auto;
    const rankedRunActive = viewModel.rankedRun !== null;
    const extremeRunActive = viewModel.extremeRun !== null;
    const challengeActive = rankedRunActive || extremeRunActive;
    this.normalMode.disabled = challengeActive;
    this.rankedMode.disabled = extremeRunActive || (!rankedRunActive && viewModel.interactionLocked);
    this.extremeMode.disabled = rankedRunActive || (!extremeRunActive && viewModel.interactionLocked);
    this.rankedRunStatus.hidden = !rankedRunActive;
    this.rankedConfigElements.forEach((element) => { element.hidden = rankedRunActive; });
    this.extremeRunStatus.hidden = !extremeRunActive;
    this.extremeConfigElements.forEach((element) => { element.hidden = extremeRunActive; });
    this.rankedEndDialog.disabled = viewModel.interactionLocked;
    if (viewModel.rankedRun !== null) {
      const run = viewModel.rankedRun;
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-player]').textContent = run.username;
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-start-balance]').textContent = formatCents(run.startBalanceCents);
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-current-balance]').textContent = formatCents(viewModel.balanceCents);
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-profit]').textContent = formatCents(viewModel.balanceCents - run.startBalanceCents);
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-x]').textContent = `${(this.displayedRankedX ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
    }
    if (viewModel.extremeRun !== null) {
      const run = viewModel.extremeRun;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-player]').textContent = run.username;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-spins]').textContent = `${run.paidSpinsCompleted} / ${EXTREME_PAID_SPINS}`;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-remaining]').textContent = String(run.paidSpinsRemaining);
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-features]').textContent = String(run.featureCount);
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-x]').textContent = `${(this.displayedExtremeX ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-final-balance]').textContent = `${formatCents(viewModel.balanceCents)} €`;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-final-x]').textContent = `${run.currentX.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
      requireElement<HTMLElement>(this.documentRoot, '[data-extreme-final-features]').textContent = String(run.featureCount);
    }
    this.root.dataset.presentationMode = viewModel.presentationMode;
    this.root.dataset.spinning = String(viewModel.spinning);
    this.root.dataset.gameMode = viewModel.gameMode;
    this.setStatus(viewModel.status, viewModel.statusTone);
    this.featureMeter.hidden = viewModel.feature === null;
    if (viewModel.feature !== null) {
      this.freeSpins.textContent = String(viewModel.feature.remainingSpins);
      this.stickyCount.textContent = String(viewModel.feature.stickyCount);
      this.featureWin.value = formatCents(viewModel.feature.totalWinCents);
    }
  }

  showDialog(kind: DialogKind): void {
    const dialog = this.dialogs.get(kind);
    if (dialog === undefined || dialog.open) return;
    dialog.showModal();
    this.callbacks.onDialogChange?.(kind, true);
  }

  closeDialog(kind: DialogKind): void {
    const dialog = this.dialogs.get(kind);
    if (dialog?.open === true) dialog.close();
  }

  setStatus(message: string, tone: StatusTone = 'neutral'): void {
    this.statusText.textContent = message;
    this.status.dataset.tone = tone;
  }

  showPotAward(awarded: number): void {
    if (this.view.feature === null || !Number.isFinite(awarded)) return;
    const current = Number.parseInt(this.freeSpins.textContent ?? '', 10);
    if (!Number.isFinite(current)) return;
    this.freeSpins.textContent = String(current + Math.max(0, Math.trunc(awarded)));
  }

  setBoardDescription(description: string): void {
    this.boardDescription.textContent = description;
  }

  destroy(): void {
    this.cleanup.splice(0).forEach((cleanup) => cleanup());
  }

  private bind(options: CreateAppShellOptions): void {
    this.listen(this.spin, 'click', () => invoke(this.callbacks.onSpin));
    this.listen(this.auto, 'click', () => this.callbacks.onAutoChange(!this.view.auto));
    this.listen(this.turbo, 'click', () => this.callbacks.onTurboChange(!this.view.turbo));
    this.listen(this.betSelect, 'change', () => this.callbacks.onBetChange(Number(this.betSelect.value)));
    this.listen(this.rankedBetSelect, 'change', () => {
      this.callbacks.onBetChange(Number(this.rankedBetSelect.value));
      requireElement<HTMLElement>(this.documentRoot, '[data-ranked-bet]').textContent = formatEuro(Number(this.rankedBetSelect.value));
    });
    this.listen(this.modeMenu, 'click', () => {
      this.setModeMenuPanel(this.view.gameMode);
      this.showDialog('ranked');
    });
    this.listen(this.normalMode, 'click', () => this.setModeMenuPanel('normal'));
    this.listen(this.rankedMode, 'click', () => this.setModeMenuPanel('ranked'));
    this.listen(this.extremeMode, 'click', () => this.setModeMenuPanel('extreme'));
    this.listen(requireElement(this.documentRoot, '[data-mode-apply]'), 'click', () => {
      this.callbacks.onModeRequest('normal');
      this.closeDialog('ranked');
    });
    this.listen(this.rankedEndDialog, 'click', () => {
      if (this.callbacks.onRankedEnd !== undefined) invoke(this.callbacks.onRankedEnd);
    });
    this.listen(requireElement(this.documentRoot, '[data-extreme-start]'), 'click', () => {
      const name = requireElement<HTMLInputElement>(this.documentRoot, '[data-extreme-name]');
      invoke(() => this.callbacks.onExtremeStart(sanitizePlayerName(name.value)));
    });
    this.listen(requireElement(this.documentRoot, '[data-extreme-abandon]'), 'click', () => this.showDialog('extreme-abandon'));
    this.listen(requireElement(this.documentRoot, '[data-extreme-abandon-confirm]'), 'click', () => {
      invoke(this.callbacks.onExtremeAbandon);
      this.closeDialog('extreme-abandon');
      this.closeDialog('ranked');
    });
    this.listen(requireElement(this.documentRoot, '[data-extreme-complete]'), 'click', () => invoke(this.callbacks.onExtremeComplete));
    this.bindDialogButton('[data-audio-button]', 'audio');
    this.bindDialogButton('[data-info-button]', 'info');
    this.listen(requireElement(this.documentRoot, '[data-ranked-info]'), 'click', () => {
      this.closeDialog('ranked');
      this.selectInfoTab('rules');
      this.showDialog('info');
    });
    this.listen(requireElement(this.documentRoot, '[data-extreme-info]'), 'click', () => {
      this.closeDialog('ranked');
      this.selectInfoTab('extreme');
      this.showDialog('info');
    });
    this.listen(requireElement(this.documentRoot, '[data-leaderboard-button]'), 'click', () => {
      this.season = 'season3';
      this.leaderboardMode = 'ranked';
      this.board = 'runs';
      this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-season-tab]').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.seasonTab === 'season3'));
      });
      this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-leaderboard-mode-tab]').forEach((button) => {
        button.disabled = false;
        button.setAttribute('aria-selected', String(button.dataset.leaderboardModeTab === 'ranked'));
        const note = button.querySelector<HTMLElement>('[data-extreme-season-note]');
        if (note !== null) note.textContent = '';
      });
      this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-board-tab]').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.boardTab === 'runs'));
      });
      this.showDialog('leaderboard');
      invoke(this.callbacks.onLeaderboardOpen);
    });
    this.bindTabs('[data-settings-tab]', '[data-settings-panel]', 'settingsTab', 'settingsPanel');
    this.bindTabs('[data-info-tab]', '[data-info-panel]', 'infoTab', 'infoPanel');
    const autoStopEnabled = requireElement<HTMLInputElement>(this.documentRoot, '[data-auto-stop-enabled]');
    const autoStopThreshold = requireElement<HTMLInputElement>(this.documentRoot, '[data-auto-stop-threshold]');
    const commitAutoStop = (): void => this.callbacks.onAutoStopChange(
      autoStopEnabled.checked,
      Math.max(1, Math.min(100_000, Math.round(Number(autoStopThreshold.value) || 50))),
    );
    this.listen(autoStopEnabled, 'change', commitAutoStop);
    this.listen(autoStopThreshold, 'change', commitAutoStop);
    this.bindRankedForm();
    this.bindLeaderboardTabs();
    this.bindReplayControls();
    this.dialogs.forEach((dialog, kind) => {
      const close = (): void => this.callbacks.onDialogChange?.(kind, false);
      dialog.addEventListener('close', close);
      this.cleanup.push(() => dialog.removeEventListener('close', close));
      const backdrop = (event: MouseEvent): void => { if (event.target === dialog) dialog.close(); };
      dialog.addEventListener('click', backdrop);
      this.cleanup.push(() => dialog.removeEventListener('click', backdrop));
    });
    this.cleanup.push(this.hotkeys.bind());
    if (options.audio !== undefined) {
      const audioSettings = new AudioSettingsController(this.documentRoot, options.audio, options.storage);
      this.cleanup.push(audioSettings.bind());
    }
    if (options.chat !== undefined) {
      const chat = new ChatDialogController(this.documentRoot, options.chat, (open) => {
        this.callbacks.onDialogChange?.('chat', open);
      });
      this.cleanup.push(chat.bind());
    }
  }

  private bindDialogButton(selector: string, kind: DialogKind): void {
    this.listen(requireElement(this.documentRoot, selector), 'click', () => this.showDialog(kind));
  }

  private bindTabs(buttonSelector: string, panelSelector: string, buttonDataset: string, panelDataset: string): void {
    const buttons = [...this.documentRoot.querySelectorAll<HTMLButtonElement>(buttonSelector)];
    const panels = [...this.documentRoot.querySelectorAll<HTMLElement>(panelSelector)];
    const activate = (button: HTMLButtonElement, moveFocus = false): void => {
      const value = button.dataset[buttonDataset];
      buttons.forEach((entry) => {
        const selected = entry === button;
        entry.setAttribute('aria-selected', String(selected));
        entry.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset[panelDataset] !== value; });
      if (moveFocus) button.focus();
    };
    buttons.forEach((button, index) => {
      this.listen(button, 'click', () => activate(button));
      this.listen<KeyboardEvent>(button, 'keydown', (event) => {
        let nextIndex: number | undefined;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;
        if (nextIndex === undefined) return;
        event.preventDefault();
        const next = buttons[nextIndex];
        if (next !== undefined) activate(next, true);
      });
    });
  }

  private selectInfoTab(value: 'paytable' | 'rules' | 'extreme'): void {
    const buttons = [...this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-info-tab]')];
    const panels = [...this.documentRoot.querySelectorAll<HTMLElement>('[data-info-panel]')];
    buttons.forEach((button) => {
      const selected = button.dataset.infoTab === value;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.infoPanel !== value; });
  }

  private setModeMenuPanel(mode: GameMode): void {
    this.normalMode.setAttribute('aria-selected', String(mode === 'normal'));
    this.rankedMode.setAttribute('aria-selected', String(mode === 'ranked'));
    this.extremeMode.setAttribute('aria-selected', String(mode === 'extreme'));
    this.documentRoot.querySelectorAll<HTMLElement>('[data-mode-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.modePanel !== mode;
    });
    this.callbacks.onModeRequest(mode);
  }

  private bindRankedForm(): void {
    const form = requireElement<HTMLFormElement>(this.documentRoot, '[data-ranked-form]');
    const name = requireElement<HTMLInputElement>(form, '[data-ranked-name]');
    const bet = requireElement<HTMLElement>(form, '[data-ranked-bet]');
    const close = requireElement<HTMLButtonElement>(form, '[data-ranked-close]');
    this.listen(close, 'click', () => this.closeDialog('ranked'));
    this.listen(form, 'submit', (event) => {
      event.preventDefault();
      if (this.extremeMode.getAttribute('aria-selected') === 'true') {
        const extremeName = requireElement<HTMLInputElement>(form, '[data-extreme-name]');
        invoke(() => this.callbacks.onExtremeStart(sanitizePlayerName(extremeName.value)));
        return;
      }
      if (this.rankedMode.getAttribute('aria-selected') !== 'true') return;
      bet.textContent = formatEuro(this.view.betCents);
      invoke(() => this.callbacks.onRankedStart(sanitizePlayerName(name.value)));
    });
  }

  private bindLeaderboardTabs(): void {
    const seasonButtons = [...this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-season-tab]')];
    const modeButtons = [...this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-leaderboard-mode-tab]')];
    const boardButtons = [...this.documentRoot.querySelectorAll<HTMLButtonElement>('[data-board-tab]')];
    const updateModeAvailability = (): void => {
      const extreme = modeButtons.find((button) => button.dataset.leaderboardModeTab === 'extreme');
      if (extreme === undefined) return;
      const archived = this.season !== 'season3';
      extreme.disabled = archived;
      const note = extreme.querySelector<HTMLElement>('[data-extreme-season-note]');
      if (note !== null) note.textContent = archived ? 'ab Season 3' : '';
      if (archived && this.leaderboardMode === 'extreme') {
        this.leaderboardMode = 'ranked';
        modeButtons.forEach((entry) => entry.setAttribute('aria-selected', String(entry.dataset.leaderboardModeTab === 'ranked')));
      }
    };
    const selectRuns = (): void => {
      this.board = 'runs';
      boardButtons.forEach((entry) => entry.setAttribute('aria-selected', String(entry.dataset.boardTab === 'runs')));
    };
    seasonButtons.forEach((button) => this.listen(button, 'click', () => {
      const value = button.dataset.seasonTab;
      if (value !== 'season1' && value !== 'season2' && value !== 'season3') return;
      this.season = value;
      seasonButtons.forEach((entry) => entry.setAttribute('aria-selected', String(entry === button)));
      updateModeAvailability();
      invoke(() => this.callbacks.onLeaderboardViewChange(this.season, this.leaderboardMode, this.board));
    }));
    modeButtons.forEach((button) => this.listen(button, 'click', () => {
      const value = button.dataset.leaderboardModeTab;
      if ((value !== 'ranked' && value !== 'extreme') || button.disabled) return;
      this.leaderboardMode = value;
      modeButtons.forEach((entry) => entry.setAttribute('aria-selected', String(entry === button)));
      selectRuns();
      invoke(() => this.callbacks.onLeaderboardViewChange(this.season, this.leaderboardMode, this.board));
    }));
    boardButtons.forEach((button) => this.listen(button, 'click', () => {
      const value = button.dataset.boardTab;
      if (value !== 'runs' && value !== 'freeSpins') return;
      this.board = value;
      boardButtons.forEach((entry) => entry.setAttribute('aria-selected', String(entry === button)));
      invoke(() => this.callbacks.onLeaderboardViewChange(this.season, this.leaderboardMode, this.board));
    }));
    updateModeAvailability();
  }

  private bindReplayControls(): void {
    this.listen(requireElement(this.documentRoot, '[data-replay-prev]'), 'click', () => this.callbacks.onReplayAction('previous'));
    this.listen(requireElement(this.documentRoot, '[data-replay-next]'), 'click', () => this.callbacks.onReplayAction('next'));
    this.listen(requireElement(this.documentRoot, '[data-replay-auto]'), 'click', () => this.callbacks.onReplayAction('toggle-auto'));
    const list = requireElement<HTMLSelectElement>(this.documentRoot, '[data-replay-list]');
    this.listen(list, 'change', () => {
      if (list.value && this.callbacks.onReplaySelect !== undefined) {
        invoke(() => this.callbacks.onReplaySelect!(list.value));
      }
    });
    const replay = this.dialogs.get('replay')!;
    const close = (): void => this.callbacks.onReplayAction('close');
    replay.addEventListener('close', close);
    this.cleanup.push(() => replay.removeEventListener('close', close));
  }

  private listen<T extends Event>(target: EventTarget, type: string, listener: (event: T) => void): void {
    const wrapped = listener as EventListener;
    target.addEventListener(type, wrapped);
    this.cleanup.push(() => target.removeEventListener(type, wrapped));
  }
}

export function createAppShell(
  documentRoot: Document,
  callbacks: AppShellCallbacks,
  options: CreateAppShellOptions = {},
): AppShellPort {
  return new AppShell(documentRoot, callbacks, options);
}
